import React, { useState, useEffect, useCallback } from 'react';
import { Search, Settings } from 'lucide-react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  updateDoc,
  doc,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { Button, Table, Input, Dropdown } from '../components/ui';
import EditTherapistModal from '../components/EditTherapistModal';
import { db } from '../lib/firebase';
import { useToast } from '../hooks/useToast';
import { Therapist, Package } from '../types/firestoreTypes';
import {
  THERAPIST_STATUS,
  THERAPIST_STATUS_OPTIONS,
  TherapistStatus,
} from '../types/constants';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 10;

const StatusPill: React.FC<{ status: TherapistStatus }> = ({ status }) => {
  const statusStyles = {
    [THERAPIST_STATUS.ACTIVE]: 'bg-[#ECFDF3] text-[#027A48]',
    [THERAPIST_STATUS.DEACTIVATED]: 'bg-[#FEF3F2] text-[#B42318]',
    [THERAPIST_STATUS.NEW_CLIENT]: 'bg-[#FFF6ED] text-[#B93815]',
    [THERAPIST_STATUS.TRIAL]: 'bg-[#F9F5FF] text-[#6941C6]',
  };

  return (
    <span
      className={`inline-flex px-2 py-1 rounded-full text-sm font-medium ${
        statusStyles[status]
      }`}
    >
      {status}
    </span>
  );
};

const AdminPage: React.FC = () => {
  const { addToast } = useToast();
  const { t, i18n } = useTranslation();
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [packageFilter, setPackageFilter] = useState('');
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedTherapist, setSelectedTherapist] = useState<Therapist | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [availablePackages, setAvailablePackages] = useState<Package[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);

  // Fetch available packages
  useEffect(() => {
    const fetchPackages = async () => {
      setIsLoadingPackages(true);
      try {
        const packagesRef = collection(db, 'packages');
        const q = query(packagesRef, orderBy('name'));
        const packagesSnapshot = await getDocs(q);
        const pkgs = packagesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...(doc.data() as Omit<Package, 'id'>)
        }));
        setAvailablePackages(pkgs);
      } catch (err) {
        console.error('Error fetching packages:', err);
        addToast({ type: 'error', message: 'Failed to load subscription packages.' });
      } finally {
        setIsLoadingPackages(false);
      }
    };
    fetchPackages();
  }, [addToast]);

  const fetchTherapists = useCallback(async (startAfterDoc?: QueryDocumentSnapshot<DocumentData>) => {
    try {
      startAfterDoc ? setIsFetchingMore(true) : setLoading(true);
      setError(null);

      let baseQuery = collection(db, 'therapists');
      let constraints: any[] = [orderBy('email')];

      if (searchTerm) {
        constraints.push(where('email', '>=', searchTerm));
        constraints.push(where('email', '<=', searchTerm + '\uf8ff'));
      }

      if (statusFilter) {
        constraints.push(where('status', '==', statusFilter));
      }

      if (packageFilter) {
        constraints.push(where('currentPackageId', '==', packageFilter));
      }

      constraints.push(limit(PAGE_SIZE));

      if (startAfterDoc) {
        constraints.push(startAfter(startAfterDoc));
      }

      const querySnapshot = await getDocs(query(baseQuery, ...constraints));
      
      const therapistData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...(doc.data() as Omit<Therapist, 'id'>)
      }));

      setTherapists(prev => startAfterDoc ? [...prev, ...therapistData] : therapistData);
      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1] || null);
      setHasMore(querySnapshot.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error('Error fetching therapists:', err);
      setError('Failed to load therapists');
      addToast({
        type: 'error',
        message: 'Failed to load therapists. Please try again.',
      });
    } finally {
      setLoading(false);
      setIsFetchingMore(false);
    }
  }, [searchTerm, statusFilter, packageFilter, addToast]);

  useEffect(() => {
    setTherapists([]);
    setLastVisible(null);
    setHasMore(true);
    fetchTherapists();
  }, [fetchTherapists]);

  const handleLoadMore = async () => {
    if (!hasMore || !lastVisible || loading || isFetchingMore) return;
    await fetchTherapists(lastVisible);
  };

  const handleEditTherapist = (therapist: Therapist) => {
    setSelectedTherapist(therapist);
    setIsEditModalOpen(true);
  };

  type TherapistUpdateData = {
    status: TherapistStatus;
    currentPackageId: string;
  };

  const handleSaveTherapist = async (data: TherapistUpdateData) => {
    if (!selectedTherapist) return;

    try {
      const therapistRef = doc(db, 'therapists', selectedTherapist.id);
      await updateDoc(therapistRef, {
        status: data.status,
        currentPackageId: data.currentPackageId,
      });

      addToast({
        type: 'success',
        message: 'Therapist updated successfully',
      });

      setIsEditModalOpen(false);
      setTherapists([]);
      setLastVisible(null);
      setHasMore(true);
      fetchTherapists();
    } catch (err) {
      console.error('Error saving therapist:', err);
      addToast({
        type: 'error',
        message: 'Failed to update therapist',
      });
    }
  };

  const packageFilterOptions = [
    { value: '', label: 'All Packages' },
    ...availablePackages.map(pkg => {
      const lang = i18n.language.split('-')[0];
      const name = (pkg as any)[`name_${lang}`] || pkg.name_en || pkg.name;
      return { value: pkg.id, label: name };
    })
  ];

  const columns = [
    { key: 'displayName', header: 'Name' },
    { key: 'email', header: 'Email' },
    {
      key: 'status',
      header: 'Status',
      render: (therapist: Therapist) => <StatusPill status={therapist.status} />,
    },
    {
      key: 'currentPackageId',
      header: 'Package',
      render: (therapist: Therapist) => {
        const lang = i18n.language.split('-')[0];
        const pkg = availablePackages.find(p => p.id === therapist.currentPackageId);
        const pkgName = pkg ? ((pkg as any)[`name_${lang}`] || pkg.name_en || pkg.name) : therapist.currentPackageId;
        return pkgName || '-';
      },
    },
    {
      key: 'createdAt',
      header: 'Joined Date',
      render: (therapist: Therapist) => {
        if (therapist.createdAt && typeof therapist.createdAt.toDate === 'function') {
          try {
            return therapist.createdAt.toDate().toLocaleDateString();
          } catch (e) {
            console.error("Error formatting createdAt date:", e, therapist.createdAt);
            return 'Invalid Date';
          }
        }
        return 'N/A';
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (therapist: Therapist) => (
        <Button
          variant="secondary"
          size="sm"
          onClick={() => handleEditTherapist(therapist)}
          leftIcon={<Settings className="w-4 h-4" />}
        >
          Manage
        </Button>
      ),
    },
  ];

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-lg text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-[#101828]">Therapist Management</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-[#EAECF0] p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Input
            placeholder="Search by email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<Search className="w-5 h-5" />}
            className="w-full sm:w-80"
          />
          
          <div className="flex gap-4">
            <Dropdown
              options={THERAPIST_STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by Status"
              className="w-full sm:w-48"
            />
            
            <Dropdown
              options={packageFilterOptions}
              value={packageFilter}
              onChange={setPackageFilter}
              placeholder="Filter by Package"
              className="w-full sm:w-48"
            />
          </div>
        </div>

        <Table
          columns={columns}
          data={therapists}
          isLoading={loading}
        />

        {hasMore && !loading && !isFetchingMore && (
          <div className="mt-4 text-center">
            <Button
              variant="secondary"
              onClick={handleLoadMore}
              isLoading={isFetchingMore}
              disabled={isFetchingMore}
            >
              Load More
            </Button>
          </div>
        )}
      </div>

      <EditTherapistModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        therapist={selectedTherapist}
        onSave={handleSaveTherapist}
        availablePackages={availablePackages}
        isLoadingPackages={isLoadingPackages}
      />
    </div>
  );
};

export default AdminPage;